import type { HttpClient } from '../http/HttpClient.ts';
import type { WorkItemDetail } from './WorkApi.ts';

interface Named {
  id: string;
  name: string;
}

export interface Board {
  id: string;
  title: string;
  archived: boolean;
  createdAt: string;
  createdBy: Named | null;
  /** The agent that made it, for createdBy. */
  createdByAgent: Named | null;
  updatedAt: string;
  updatedBy: Named | null;
  updatedByAgent: Named | null;
  /** When its thumbnail was made (null: none yet). */
  thumbnailAt: string | null;
}

export interface BoardVersion {
  id: string;
  createdAt: string;
  elementCount: number;
  authors: Named[];
}

export interface NotesToItems {
  notes: { elementId: string; text: string }[];
  type: 'story' | 'task' | 'bug';
  epicId?: string | null;
  sprintId?: string | null;
}

const base = (org: string, key: string) =>
  `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}/boards`;

/** A project's whiteboards. */
export class BoardsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  list(
    org: string,
    key: string,
    query: { q?: string; archived?: boolean; item?: string } = {},
  ): Promise<{ boards: Board[] }> {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.archived) params.set('archived', 'true');
    // Only the boards this item (its id) is drawn on.
    if (query.item) params.set('item', query.item);
    const qs = params.toString();
    return this.#http.get(`${base(org, key)}${qs ? `?${qs}` : ''}`);
  }

  get(org: string, key: string, id: string): Promise<{ board: Board }> {
    return this.#http.get(`${base(org, key)}/${encodeURIComponent(id)}`);
  }

  create(org: string, key: string, title: string): Promise<{ board: Board }> {
    return this.#http.post(base(org, key), { title });
  }

  update(
    org: string,
    key: string,
    id: string,
    change: { title?: string; archived?: boolean },
  ): Promise<{ board: Board }> {
    return this.#http.patch(`${base(org, key)}/${encodeURIComponent(id)}`, change);
  }

  /** Store an image placed on the board (Excalidraw's file id). */
  uploadFile(
    org: string,
    key: string,
    id: string,
    fileId: string,
    blob: Blob,
  ): Promise<{ file: { fileId: string; mimeType: string; url: string } }> {
    return this.#http.post(
      `${base(org, key)}/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`,
      undefined,
      { raw: blob },
    );
  }

  putThumbnail(org: string, key: string, id: string, png: Blob): Promise<void> {
    return this.#http.put(`${base(org, key)}/${encodeURIComponent(id)}/thumbnail`, undefined, {
      raw: png,
    });
  }

  thumbnailUrl(org: string, key: string, id: string, at: string): string {
    return this.#http.url(
      `${base(org, key)}/${encodeURIComponent(id)}/thumbnail?v=${encodeURIComponent(at)}`,
    );
  }

  /** An image on a board (its id, as the drawing lists it). */
  file(org: string, key: string, id: string, fileId: string): Promise<Blob> {
    return this.#http.blob(
      `${base(org, key)}/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`,
    );
  }

  /** Sticky notes → work items (one per note; the page puts each card where its note was). */
  convert(
    org: string,
    key: string,
    id: string,
    body: NotesToItems,
  ): Promise<{ items: { elementId: string; item: WorkItemDetail }[] }> {
    return this.#http.post(`${base(org, key)}/${encodeURIComponent(id)}/convert`, body);
  }

  versions(org: string, key: string, id: string): Promise<{ versions: BoardVersion[] }> {
    return this.#http.get(`${base(org, key)}/${encodeURIComponent(id)}/versions`);
  }

  restore(org: string, key: string, id: string, versionId: string): Promise<void> {
    return this.#http.post(
      `${base(org, key)}/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`,
    );
  }

  remove(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${base(org, key)}/${encodeURIComponent(id)}`);
  }
}
